import { useState } from 'react';
import { Flex, Heading, Button, Callout } from '@radix-ui/themes';
import { PlusIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useGetDiscordServersQuery } from '../../graphql/queries/GetDiscordServers.generated';
import { useGetWebhooksQuery } from '../../graphql/queries/GetWebhooks.generated';
import { useGetSlackInstallationsQuery } from '../../graphql/queries/GetSlackInstallations.generated';
import { useDeleteDiscordServerMutation } from '../../graphql/mutations/DeleteDiscordServer.generated';
import { useDeleteWebhookMutation } from '../../graphql/mutations/DeleteWebhook.generated';
import { useDeleteSlackInstallationMutation } from '../../graphql/mutations/DeleteSlackInstallation.generated';
import { useTestDiscordServerMutation } from '../../graphql/mutations/TestDiscordServer.generated';
import { useTestWebhookMutation } from '../../graphql/mutations/TestWebhook.generated';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../utils/toast';
import DiscordServerDialog from '../../components/DiscordServerDialog';
import WebhookDialog from '../../components/WebhookDialog';
import SlackInstallationDialog from '../../components/SlackInstallationDialog';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import IntegrationCard from '../../components/IntegrationCard';
import SkeletonCard from '../../components/IntegrationCard/SkeletonCard';
import DiscordBotWizard from '../../components/DiscordBotWizard';
import SlackInstallationWizard from '../../components/SlackInstallationWizard';

export default function IntegrationsPage() {
  const { user } = useAuth();

  const [discordDialogOpen, setDiscordDialogOpen] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [slackInstallationDialogOpen, setSlackInstallationDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discordWizardOpen, setDiscordWizardOpen] = useState(false);
  const [slackWizardOpen, setSlackWizardOpen] = useState(false);
  const [selectedDiscordServer, setSelectedDiscordServer] = useState<any>(null);
  const [selectedWebhook, setSelectedWebhook] = useState<any>(null);
  const [selectedSlackInstallation, setSelectedSlackInstallation] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'discord' | 'webhook' | 'slack';
    id: string;
    name: string;
  } | null>(null);

  const {
    data: discordData,
    loading: discordLoading,
    error: discordError,
  } = useGetDiscordServersQuery({
    variables: { ownerUserId: user?.id },
  });

  const {
    data: webhooksData,
    loading: webhooksLoading,
    error: webhooksError,
  } = useGetWebhooksQuery({
    variables: { ownerUserId: user?.id },
  });

  const {
    data: slackData,
    loading: slackLoading,
    error: slackError,
  } = useGetSlackInstallationsQuery();

  const [deleteDiscordServer, { loading: deletingDiscord }] =
    useDeleteDiscordServerMutation();
  const [deleteWebhook, { loading: deletingWebhook }] =
    useDeleteWebhookMutation();
  const [deleteSlackInstallation, { loading: deletingSlack }] =
    useDeleteSlackInstallationMutation();
  const [testDiscordServer] = useTestDiscordServerMutation();
  const [testWebhook] = useTestWebhookMutation();

  const discordServers =
    discordData?.discordServers?.edges?.map((edge) => edge.node) || [];
  const webhooks =
    webhooksData?.webhooks?.edges?.map((edge) => edge.node) || [];
  const slackInstallations = slackData?.slackInstallations?.edges?.map((edge) => edge.node) || [];

  const allIntegrations = [
    ...discordServers.map((server) => ({
      ...server,
      integrationType: 'discord' as const,
      displayName: server.name,
    })),
    ...slackInstallations.map((installation) => ({
      ...installation,
      integrationType: 'slack' as const,
      displayName: installation.name,
    })),
    ...webhooks.map((webhook) => ({
      ...webhook,
      integrationType: 'webhook' as const,
      displayName: webhook.label,
    })),
  ];

  const handleAddDiscordServer = () => {
    setDiscordWizardOpen(true);
  };

  const handleSkipDiscordWizard = () => {
    setSelectedDiscordServer(null);
    setDiscordDialogOpen(true);
  };

  const handleEditDiscordServer = (server: any) => {
    setSelectedDiscordServer(server);
    setDiscordDialogOpen(true);
  };

  const handleDeleteDiscordServer = (server: any) => {
    setDeleteTarget({ type: 'discord', id: server.id, name: server.name });
    setDeleteDialogOpen(true);
  };

  const handleAddWebhook = () => {
    setSelectedWebhook(null);
    setWebhookDialogOpen(true);
  };

  const handleEditWebhook = (webhook: any) => {
    setSelectedWebhook(webhook);
    setWebhookDialogOpen(true);
  };

  const handleDeleteWebhook = (webhook: any) => {
    setDeleteTarget({ type: 'webhook', id: webhook.id, name: webhook.label });
    setDeleteDialogOpen(true);
  };

  const handleEditSlackInstallation = (installation: any) => {
    setSelectedSlackInstallation(installation);
    setSlackInstallationDialogOpen(true);
  };

  const handleDeleteSlackInstallation = (installation: any) => {
    setDeleteTarget({ type: 'slack', id: installation.id, name: installation.name });
    setDeleteDialogOpen(true);
  };

  const handleTestIntegration = async (integration: any) => {
    const type = integration.integrationType;
    const name = integration.displayName;

    // Show testing toast
    toast.info(`Testing ${type} integration "${name}"...`);

    try {
      let result;
      if (type === 'discord') {
        const { data } = await testDiscordServer({
          variables: { id: integration.id },
        });
        result = data?.testDiscordServer;
      } else if (type === 'webhook') {
        const { data } = await testWebhook({
          variables: { id: integration.id },
        });
        result = data?.testWebhook;
      }

      if (result?.success) {
        toast.success(result.message);
      } else {
        toast.error(result?.message || 'Test failed');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An unknown error occurred';
      toast.error(`Test failed: ${errorMessage}`);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.type === 'discord') {
        await deleteDiscordServer({
          variables: { id: deleteTarget.id },
          refetchQueries: ['GetDiscordServers'],
        });
        toast.success(
          `Successfully deleted Discord server "${deleteTarget.name}"`,
        );
      } else if (deleteTarget.type === 'slack') {
        await deleteSlackInstallation({
          variables: { id: deleteTarget.id },
          refetchQueries: ['GetSlackInstallations'],
        });
        toast.success(`Successfully deleted Slack workspace "${deleteTarget.name}"`);
      } else {
        await deleteWebhook({
          variables: { id: deleteTarget.id },
          refetchQueries: ['GetWebhooks'],
        });
        toast.success(`Successfully deleted webhook "${deleteTarget.name}"`);
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'An unknown error occurred';
      const typeLabel =
        deleteTarget.type === 'discord'
          ? 'Discord server'
          : deleteTarget.type === 'slack'
            ? 'Slack workspace'
            : 'webhook';
      toast.error(`Failed to delete ${typeLabel}: ${errorMessage}`);
    }
  };

  const loading = discordLoading || webhooksLoading || slackLoading;
  const error = discordError || webhooksError || slackError;
  const hasIntegrations = allIntegrations.length > 0;

  return (
    <Flex direction="column" gap="4">
      {/* Header */}
      <Flex justify="between" align="center">
        <Heading size="6">Integrations</Heading>
        <Flex gap="2">
          <Button size="2" variant="outline" onClick={handleAddDiscordServer}>
            <PlusIcon /> Discord
          </Button>
          <Button
            size="2"
            variant="outline"
            onClick={() => setSlackWizardOpen(true)}
          >
            <PlusIcon /> Slack
          </Button>
          <Button size="2" variant="outline" onClick={handleAddWebhook}>
            <PlusIcon /> Webhook
          </Button>
        </Flex>
      </Flex>

      {/* Error State */}
      {error && (
        <Callout.Root color="red" style={{ marginTop: '8px' }}>
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>
            Failed to load integrations. {error.message}
          </Callout.Text>
        </Callout.Root>
      )}

      {/* Loading State with Skeleton Cards */}
      {loading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
            marginTop: '8px',
          }}
        >
          {Array.from({ length: 3 }).map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !hasIntegrations && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="4"
          role="region"
          aria-label="Empty integrations state"
          style={{
            minHeight: '400px',
            border: '2px dashed rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            padding: '48px',
          }}
        >
          <div style={{ fontSize: '48px' }}>🔌</div>
          <Heading size="5">No Integrations Yet</Heading>
          <p
            style={{
              color: '#6b7280',
              textAlign: 'center',
              maxWidth: '400px',
              margin: 0,
            }}
          >
            Connect Discord, Slack, or webhooks to send notifications from
            workflows
          </p>
          <Flex gap="3" style={{ marginTop: '16px' }}>
            <Button onClick={handleAddDiscordServer}>Add Discord</Button>
            <Button variant="outline" onClick={() => setSlackWizardOpen(true)}>
              Add Slack
            </Button>
            <Button variant="outline" onClick={handleAddWebhook}>
              Add Webhook
            </Button>
          </Flex>
        </Flex>
      )}

      {/* Card Grid */}
      {!loading && hasIntegrations && (
        <div
          role="list"
          aria-label="Integrations list"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
            marginTop: '8px',
            animation: 'fadeIn 0.3s ease-in',
          }}
        >
          {allIntegrations.map((integration, idx) => {
            if (integration.integrationType === 'discord') {
              return (
                <IntegrationCard
                  key={integration.id}
                  type="discord"
                  name={integration.displayName}
                  status="connected"
                  metadata={{}}
                  onEdit={() => handleEditDiscordServer(integration)}
                  onTest={() => handleTestIntegration(integration)}
                  onDelete={() => handleDeleteDiscordServer(integration)}
                />
              );
            } else if (integration.integrationType === 'slack') {
              return (
                <IntegrationCard
                  key={integration.id}
                  type="slack"
                  name={integration.displayName}
                  status="connected"
                  metadata={{}}
                  onClick={() => handleEditSlackInstallation(integration)}
                  onDelete={() => handleDeleteSlackInstallation(integration)}
                />
              );
            } else {
              return (
                <IntegrationCard
                  key={integration.id}
                  type="webhook"
                  name={integration.displayName}
                  status="connected"
                  metadata={{
                    webhookType: integration.type,
                    httpMethod: integration.method,
                  }}
                  onEdit={() => handleEditWebhook(integration)}
                  onTest={() => handleTestIntegration(integration)}
                  onDelete={() => handleDeleteWebhook(integration)}
                />
              );
            }
          })}
        </div>
      )}

      {/* Wizards */}
      <DiscordBotWizard
        open={discordWizardOpen}
        onOpenChange={setDiscordWizardOpen}
        onSkip={handleSkipDiscordWizard}
      />

      <SlackInstallationWizard
        open={slackWizardOpen}
        onOpenChange={setSlackWizardOpen}
      />

      {/* Dialogs */}
      <DiscordServerDialog
        open={discordDialogOpen}
        onOpenChange={setDiscordDialogOpen}
        server={selectedDiscordServer}
      />

      <WebhookDialog
        open={webhookDialogOpen}
        onOpenChange={setWebhookDialogOpen}
        webhook={selectedWebhook}
      />

      <SlackInstallationDialog
        open={slackInstallationDialogOpen}
        onOpenChange={setSlackInstallationDialogOpen}
        slackInstallation={selectedSlackInstallation}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={`Delete ${
          deleteTarget?.type === 'discord'
            ? 'Discord Server'
            : deleteTarget?.type === 'slack'
              ? 'Slack Workspace'
              : 'Webhook'
        }`}
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        loading={deletingDiscord || deletingWebhook || deletingSlack}
      />

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Flex>
  );
}
